import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { api, API_VERSION_TWO } from 'apis/axiosInstance'
import { Toastr } from 'components/global/Toastr'
import { HTTPValidationError } from 'types/httpTypes'
import { TAnnouncementRecipientsAxiosResponse } from '../announcementSegrigation/useAnnouncementSeg.type'
import {
  announcementsResponse,
  AnnouncementUpdateType,
  CreateAnnouncementFollowUpType,
  CreateAnnouncementFollowUpTypeT,
  CreateAnnouncementType,
  CreateAnnouncementTypeT,
  GetAnnouncementDetailsType,
  GetCommentsType,
  GetTemplatesType,
  UpdateAnnouncementType,
} from './useAnnouncement.Type'

// ? query keys
export const announcementsKey = (params?: {
  filter: { status: string; is_all_employee?: boolean }
  page?: number
  rpp?: number
}): QueryKey => ['get_all_announcements', params]
export const getCommentsKey = (params?: {
  commentable_type: string
  commentable_id: number
}): QueryKey => ['get_comments', params]

export const getCommentsKeyWithPagination = (params?: {
  commentable_type: string
  commentable_id: number
}): QueryKey => ['get_comments_paginated', params]

// ? functions
const getAllAnnouncements = (params?: {
  filter: { status: string; is_all_employee?: boolean }
  page?: number
  rpp?: number
}) => {
  return api.get(`/${API_VERSION_TWO}/hr/announcements`, { params })
}
const getComments = (params?: { commentable_type: string; commentable_id: number }) => {
  return api.get(`/${API_VERSION_TWO}/comments`, { params })
}

const getCommentsWithPagination = (params: {
  commentable_type?: string
  commentable_id?: number
  page?: number
  rpp?: number
}) => {
  return api.get(`/${API_VERSION_TWO}/comments`, { params })
}

const getTemplates = () => {
  return api.get(`/${API_VERSION_TWO}/hr/announcements/templates`)
}

const handleCreateAnnouncement = (data: CreateAnnouncementType) => {
  return api.post(`/${API_VERSION_TWO}/hr/announcements`, data)
}
const handleCreateFollowUpAnnouncement = (id: number, payload: CreateAnnouncementFollowUpType) => {
  return api.post(`/${API_VERSION_TWO}/hr/announcements/${id}/follow_up`, payload)
}
const handleUpdateAnnouncement = (id: string, data: UpdateAnnouncementType) => {
  return api.put(`/${API_VERSION_TWO}/hr/announcements/${id}`, data)
}

const handleUnpublishAnnouncement = (id: number) => {
  return api.put(`/${API_VERSION_TWO}/hr/announcements/${id}/unpublish`)
}

const handlePublishAnnouncement = (id: number) => {
  return api.put(`/${API_VERSION_TWO}/hr/announcements/${id}/publish`)
}

export const getAnnouncementDetailsKey = (params?: { id: string }): QueryKey => [
  'announcement_details',
  params,
]
export const getSingleAnnouncementKey = (params?: { id: string }): QueryKey => [
  'single_announcement',
  params,
]
export const getTemplatesKey = (): QueryKey => ['announcement_templates']

const handleSingleAnnouncement = (params?: { id: string }) => {
  return api.get(`/${API_VERSION_TWO}/hr/announcements/${params?.id}`)
}
const handleAnnouncementDetails = (params?: { id: string }) => {
  return api.get(`/${API_VERSION_TWO}/hr/announcements/${params?.id}/details`)
}
// ? hooks
export const useGetAllAnnouncement = <TData = announcementsResponse, TError = HTTPValidationError>(
  params: {
    filter: { status: string; is_all_employee?: boolean }
    page?: number
    rpp?: number
    search?: string
  },
  opts?: {
    query?: UseQueryOptions<TAnnouncementRecipientsAxiosResponse, TError, TData>
  },
): UseQueryResult<TData, TError> => {
  return useQuery({
    ...(opts?.query || {}),
    queryKey: opts?.query?.queryKey ?? announcementsKey(params),
    queryFn: () => getAllAnnouncements(params),
  })
}

export const useGetComments = <TData = GetCommentsType, TError = HTTPValidationError>(opts?: {
  query?: UseQueryOptions<GetCommentsType, TError, TData>
  params: {
    commentable_type: string
    commentable_id: number
    rpp?: number
  }
}): UseQueryResult<TData, TError> => {
  return useQuery({
    ...(opts?.query || {}),
    queryKey: opts?.query?.queryKey ?? getCommentsKey(opts?.params),
    queryFn: () => getComments(opts?.params),
  })
}

export const useGetCommentsWithPagination = <
  TData = GetCommentsType,
  TError = HTTPValidationError,
>(opts?: {
  query?: UseInfiniteQueryOptions<GetCommentsType, TError, TData>
  params: {
    commentable_type: string
    commentable_id: number
    page?: number
    rpp?: number
  }
}): UseInfiniteQueryResult<TData, TError> => {
  return useInfiniteQuery({
    ...(opts?.query || {}),
    queryKey: opts?.query?.queryKey ?? getCommentsKeyWithPagination(opts?.params),
    enabled: !!opts?.params?.commentable_id,
    queryFn: ({ pageParam = 1 }) =>
      getCommentsWithPagination({
        commentable_type: opts?.params?.commentable_type,
        commentable_id: opts?.params?.commentable_id,
        page: pageParam,
        rpp: opts?.params?.rpp,
      }),
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage?.data?.data?.pagination?.next_page
      return nextPage
    },
    keepPreviousData: true,
  })
}

export const useCreateAnnouncement = <TError extends HTTPValidationError>(opts?: {
  mutation?: UseMutationOptions<CreateAnnouncementTypeT, TError, CreateAnnouncementType>
}): UseMutationResult<CreateAnnouncementTypeT, TError, CreateAnnouncementType> => {
  return useMutation({
    ...(opts?.mutation || {}),
    mutationFn: (payload) => handleCreateAnnouncement(payload),
    onSuccess: (res, variables, context) => {
      opts?.mutation?.onSuccess?.(res, variables, context)
      Toastr.success(res.data.message)
    },
    onError: (error) => {
      Toastr.error(error?.response?.data?.error)
    },
  })
}

export const useUnpublishAnnouncement = <TError extends HTTPValidationError>(opts?: {
  mutation?: UseMutationOptions<TAnnouncementRecipientsAxiosResponse, TError, { id: number | null }>
}): UseMutationResult<TAnnouncementRecipientsAxiosResponse, TError, { id: number | null }> => {
  return useMutation<TAnnouncementRecipientsAxiosResponse, TError, { id: number | null }>({
    ...(opts?.mutation || {}),
    mutationFn: async ({ id }) => {
      if (id === null) {
        throw new Error('ID cannot be null')
      }
      return handleUnpublishAnnouncement(id) // Return the response for onSuccess to use
    },
    onSuccess: (res, variables, context) => {
      if (res.data.message) {
        Toastr.success(res.data.message)
      }

      opts?.mutation?.onSuccess?.(res, variables, context)
    },
    onError: (error) => {
      Toastr.error(error?.response?.data?.error || 'An error occurred')
    },
  })
}

export const usePublishAnnouncement = <TError extends HTTPValidationError>(opts?: {
  mutation?: UseMutationOptions<TAnnouncementRecipientsAxiosResponse, TError, { id: number }>
}): UseMutationResult<TAnnouncementRecipientsAxiosResponse, TError, { id: number }> => {
  return useMutation({
    ...(opts?.mutation || {}),
    mutationFn: ({ id }) => {
      if (id == null) {
        throw new Error('Invalid ID: ID must not be null or undefined.')
      }
      return handlePublishAnnouncement(id)
    },
    onSuccess: (res, variables, context) => {
      if (res.data.message) {
        Toastr.success(res.data.message)
      }
      opts?.mutation?.onSuccess?.(res, variables, context)
    },
    onError: (error) => {
      Toastr.error(error?.response?.data?.error)
    },
  })
}

export const useUpdateAnnouncement = <TError extends HTTPValidationError>(opts?: {
  mutation?: UseMutationOptions<
    AnnouncementUpdateType,
    TError,
    { id: string; payload: UpdateAnnouncementType }
  >
}): UseMutationResult<
  AnnouncementUpdateType,
  TError,
  { id: string; payload: UpdateAnnouncementType }
> => {
  return useMutation({
    ...(opts?.mutation || {}),
    mutationFn: (data) => handleUpdateAnnouncement(data.id, data.payload),
    onSuccess: (res, variables, context) => {
      opts?.mutation?.onSuccess?.(res, variables, context)
      Toastr.success(res.data.message)
    },
    onError: (error) => {
      Toastr.error(error?.response?.data?.error)
    },
  })
}

export const useAnnouncementDetails = <
  TData = GetAnnouncementDetailsType,
  TError = HTTPValidationError,
>(opts?: {
  query: UseQueryOptions<GetAnnouncementDetailsType, TError, TData>
  params: { id: string }
}): UseQueryResult<TData, TError> => {
  return useQuery({
    ...(opts?.query || {}),
    queryKey: opts?.query?.queryKey ?? getAnnouncementDetailsKey(opts?.params),
    queryFn: () => handleAnnouncementDetails(opts?.params),
  })
}

export const useSingleAnnouncement = <
  TData = GetAnnouncementDetailsType,
  TError = HTTPValidationError,
>(opts?: {
  query: UseQueryOptions<GetAnnouncementDetailsType, TError, TData>
  params: { id: string }
}): UseQueryResult<TData, TError> => {
  return useQuery({
    ...(opts?.query || {}),
    queryKey: opts?.query?.queryKey ?? getSingleAnnouncementKey(opts?.params),
    queryFn: () => handleSingleAnnouncement(opts?.params),
  })
}

export const useAnnouncementTemplates = <
  TData = GetTemplatesType,
  TError = HTTPValidationError,
>(opts?: {
  query: UseQueryOptions<GetTemplatesType, TError, TData>
}): UseQueryResult<TData, TError> => {
  return useQuery({
    ...(opts?.query || {}),
    queryKey: opts?.query?.queryKey ?? getTemplatesKey(),
    queryFn: () => getTemplates(),
  })
}

export const useCreateFollowUpAnnouncement = <TError extends HTTPValidationError>(opts?: {
  mutation?: UseMutationOptions<
    CreateAnnouncementFollowUpTypeT,
    TError,
    { id: number; announcement: CreateAnnouncementFollowUpType['announcement'] }
  >
}): UseMutationResult<
  CreateAnnouncementFollowUpTypeT,
  TError,
  { id: number; announcement: CreateAnnouncementFollowUpType['announcement'] }
> => {
  return useMutation({
    ...(opts?.mutation || {}),
    mutationFn: (payload) =>
      handleCreateFollowUpAnnouncement(payload.id, { announcement: payload.announcement }),
    onSuccess: (res, variables, context) => {
      opts?.mutation?.onSuccess?.(res, variables, context)
      Toastr.success(res.data.message)
    },
    onError: (error) => {
      Toastr.error(error?.response?.data?.error)
    },
  })
}
